import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { generateDefaultValues, schema, Schema } from "./schema";
import { ProjectData } from "../../../../models/ProjectData";
import DataSvc from "../../../../services/dataSvc";
import { HookForm } from "../../../HookForm/HookForm";
import { Input } from "../../../HookForm/Input";

interface IEditProjectDetailsFormProps {
  handleCancel: () => void;
  project: ProjectData;
}

export const EditProjectDetailsForm: React.FunctionComponent<
  IEditProjectDetailsFormProps
> = (props) => {
  const { handleCancel, project } = props;
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(
      `projectManagementPage.editProjectDetailsModal.editProjectDetailsForm.${key}`
    );

  const queryClient = useQueryClient();

  const defaultValues = useMemo(() => {
    return generateDefaultValues(project);
  }, [project]);

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const onSubmit = async (data: Schema) => {
    const payload = {
      name: data.project_name,
      // If target_facility_count is 0, set it to undefined
      target_facility_count:
        data.target_facility_count > 0 ? data.target_facility_count : null,
    };
    try {
      await DataSvc.updateProject({
        organizationId: project.organization_id,
        projectId: project.id.toString(),
        payload,
      });
      toast.success(t("success"));
      // Invalidate queries so data is refreshed
      queryClient.invalidateQueries({
        queryKey: ["projects", project.organization_id],
      });
      handleCancel();
    } catch (error) {
      // Do nothing, axios will handle the error
    }
  };

  return (
    <div className="edit-project-details-form">
      <HookForm
        onCancel={handleCancel}
        onSubmit={onSubmit}
        form={form}
        submitButtonLabel={t("update")}
        showSubmitButton={true}
        showCancelButton={true}
      >
        <Input
          id="project_name"
          name="project_name"
          label={t("project_name")}
        />
        <Input
          id="target_facility_count"
          name="target_facility_count"
          label={t("target_facility_count")}
          autoComplete="off"
        />
      </HookForm>
    </div>
  );
};
